import { useCallback, useEffect, useState } from 'react'
import { useGetSpaceCalendar } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

import { DraggableTimeslot } from './draggable-timeslot'
import { DraggableTimeslotContainer } from './draggable-timeslot-container'
import {
	calculateMinutes,
	calculateY,
	formatTime,
	parseTime,
	snapToGrid,
	TIME_SLOT_HEIGHT,
	type TimeslotBounds,
} from './helpers'
import { useCustomModifiers } from './use-custom-modifiers'
import type { BookingOption } from '../step-time'

const computeBoundsFromTimeSlot = (timeslot: BookingOption) => {
	if (!timeslot.start || !timeslot.end) {
		return {
			height: TIME_SLOT_HEIGHT * 2,
			startY: calculateY(parseTime('08:00')),
		}
	}

	const startY = calculateY(parseTime(timeslot.start))
	const height = calculateY(parseTime(timeslot.end)) - startY

	return {
		height,
		startY,
	}
}

export const TimeScheduler = ({
	spaceId,
	eventId,
	value,
	isAvailable,
	onTimeChange,
}: {
	spaceId: string
	eventId?: string
	value: BookingOption
	isAvailable?: boolean
	notAvailableMsg?: string
	onTimeChange: (start: string, end: string) => void
}) => {
	const [isTransforming, setIsTransforming] = useState(false)

	const [bounds, setBounds] = useState<TimeslotBounds>(() =>
		computeBoundsFromTimeSlot(value),
	)

	const customModifiers = useCustomModifiers(bounds)

	const calendarQuery = useGetSpaceCalendar(spaceId, {
		day: value.day,
		ignore: eventId,
	})

	useEffect(() => {
		setBounds(computeBoundsFromTimeSlot(value))
	}, [value])

	const handleDragEnd = useCallback(
		({ active, delta }: DragEndEvent) => {
			setBounds((prev) => {
				let newStartY = prev.startY
				let newHeight = prev.height

				switch (active.id) {
					case 'draggable-timeslot':
						newStartY = prev.startY + delta.y
						break

					case 'draggable-timeslot-resize-top':
						newStartY = prev.startY + delta.y
						newHeight = prev.height - delta.y
						break

					case 'draggable-timeslot-resize-bottom':
						newHeight = prev.height + delta.y
						break
				}

				newStartY = snapToGrid(newStartY)
				newStartY = Math.max(0, newStartY)

				newHeight = snapToGrid(newHeight)
				newHeight = Math.max(TIME_SLOT_HEIGHT, newHeight)

				const newStartMinutes = calculateMinutes(newStartY)
				const newEndMinutes = calculateMinutes(newStartY + newHeight)
				const newStart = formatTime(newStartMinutes)
				const newEnd = formatTime(newEndMinutes)

				onTimeChange(newStart, newEnd)

				return { ...prev, height: newHeight, startY: newStartY }
			})

			setTimeout(() => setIsTransforming(false), 100)
		},
		[onTimeChange],
	)

	const handleTimeChange = useCallback(
		(newStart: string, newEnd: string) => {
			const newStartY = calculateY(parseTime(newStart))
			const newHeight = calculateY(parseTime(newEnd)) - newStartY

			setBounds({
				height: newHeight,
				startY: newStartY,
			})

			onTimeChange(newStart, newEnd)
		},
		[onTimeChange],
	)

	return (
		<DndContext
			autoScroll={false}
			modifiers={[restrictToVerticalAxis, customModifiers]}
			onDragEnd={handleDragEnd}
			onDragStart={() => setIsTransforming(true)}
		>
			{calendarQuery.isSuccess && (
				<DraggableTimeslotContainer
					bounds={bounds}
					calendar={calendarQuery.data.data}
					onTimeChange={handleTimeChange}
					value={value}
				>
					<DraggableTimeslot
						bounds={bounds}
						isAvailable={isAvailable}
						isTransforming={isTransforming}
						value={value}
					/>
				</DraggableTimeslotContainer>
			)}
		</DndContext>
	)
}
